/**
 * CMP同意状態のCookie読み書き
 *
 * 単一のJSON Cookie（デフォルト: ck_{tenantId}_cmp）に同意状態を保存する。
 * Cookie保存に失敗した場合はメモリ上に保持し、現在のセッション内で動作させる。
 *
 * Cookieの構造（同意とリージョン判定結果を1つのCookieに統合）:
 * {
 *   "id": "uuid", "categories": {...}, "timestamp": "ISO8601",  // 同意操作後のみ
 *   "geo": { "country": "JP", "resolvedAt": "ISO8601" }          // 国判定後（同意前から書ける）
 * }
 */

// Cookie保存に失敗した場合のメモリフォールバック
let memoryFallback = null;

// geo情報の鮮度TTL（日数）
const GEO_TTL_DAYS = 7;

/**
 * UUID v4を生成する
 * crypto.randomUUID()を優先し、非対応ブラウザではcrypto.getRandomValues()にフォールバック
 * @returns {string}
 */
export function generateUUID() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // crypto.getRandomValues() ベースのフォールバック
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 1
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return [hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20, 32)].join('-');
}

/**
 * Cookie全体をパースして返す（同意の妥当性検証なし）
 * @param {string} cookieName
 * @returns {Object|null} パース済みオブジェクト（壊れたJSON・非オブジェクトはnull）
 */
export function readRaw(cookieName) {
  try {
    const match = document.cookie.split('; ').find((row) => row.startsWith(cookieName + '='));
    if (!match) return null;

    const value = decodeURIComponent(match.split('=').slice(1).join('='));
    const parsed = JSON.parse(value);

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * 同意状態をCookieから読み取る
 * geoのみのCookie（id/categoriesなし）は同意状態としてはnullを返す
 * @param {string} cookieName
 * @returns {Object|null} ConsentState or null
 */
export function readConsent(cookieName) {
  // メモリフォールバックがあればそちらを返す
  if (memoryFallback) {
    return memoryFallback;
  }

  const parsed = readRaw(cookieName);
  if (!parsed || !parsed.id || !parsed.categories) return null;
  // categories がプレーンオブジェクトであることを検証（配列や文字列等を拒否）
  if (typeof parsed.categories !== 'object' || Array.isArray(parsed.categories)) return null;
  return parsed;
}

/**
 * リージョン判定結果（geo）をCookieから読み取る
 * @param {string} cookieName
 * @returns {Object|null} { country, resolvedAt } or null
 */
export function readGeo(cookieName) {
  const parsed = readRaw(cookieName);
  if (!parsed || !parsed.geo) return null;
  if (typeof parsed.geo !== 'object' || Array.isArray(parsed.geo)) return null;
  return parsed.geo;
}

/**
 * リージョン判定結果（geo）をCookieに書き込む
 * 既存の同意状態（id/categories/timestamp）は保持し、geoフィールドのみ差し替える
 * @param {string} cookieName
 * @param {string|null} country - ISO国コード（判定不能時null）
 * @param {Object} cookieOptions - { maxAge, path, sameSite }
 */
export function writeGeo(cookieName, country, cookieOptions) {
  const raw = readRaw(cookieName) || {};
  const next = {
    ...raw,
    geo: {
      country: typeof country === 'string' && country ? country : null,
      resolvedAt: new Date().toISOString()
    }
  };

  try {
    document.cookie = buildCookieString(cookieName, next, cookieOptions);
  } catch {
    // geoはCookieに保存できなければ次回再判定するだけなので、失敗は黙って無視
  }
}

/**
 * geo情報が新鮮（TTL内）かを判定する
 * @param {Object|null} geo - { country, resolvedAt }
 * @param {number} [ttlDays=7]
 * @returns {boolean}
 */
export function isGeoFresh(geo, ttlDays = GEO_TTL_DAYS) {
  if (!geo || typeof geo.resolvedAt !== 'string') return false;
  const resolvedAt = Date.parse(geo.resolvedAt);
  if (Number.isNaN(resolvedAt)) return false;
  return Date.now() - resolvedAt < ttlDays * 86400000;
}

/**
 * 同意状態をCookieに書き込む
 * 既存のgeoフィールドは保持する（同意書き込みでリージョン判定結果が消えないように）
 * @param {string} cookieName
 * @param {Object} consent - ConsentState
 * @param {Object} cookieOptions - { maxAge, path, sameSite }
 */
export function writeConsent(cookieName, consent, cookieOptions) {
  const raw = readRaw(cookieName);
  const merged = raw && raw.geo ? { ...consent, geo: raw.geo } : consent;
  const cookieStr = buildCookieString(cookieName, merged, cookieOptions);

  try {
    document.cookie = cookieStr;
    // 書き込み成功を検証（document.cookieは例外をスローしないため）
    if (document.cookie.includes(cookieName + '=')) {
      memoryFallback = null;
    } else {
      memoryFallback = merged;
    }
  } catch {
    // Cookie保存失敗 — メモリ上で保持
    memoryFallback = merged;
  }
}

/**
 * Set-Cookie文字列を組み立てる
 * @param {string} cookieName
 * @param {Object} value - JSONシリアライズするオブジェクト
 * @param {Object} cookieOptions - { maxAge, path, sameSite }
 * @returns {string}
 */
function buildCookieString(cookieName, value, cookieOptions) {
  const json = JSON.stringify(value);
  const maxAgeSeconds = cookieOptions.maxAge * 86400;
  const parts = [
    `${cookieName}=${encodeURIComponent(json)}`,
    `max-age=${maxAgeSeconds}`,
    `path=${cookieOptions.path}`,
    `SameSite=${cookieOptions.sameSite}`
  ];
  // HTTPS接続時はSecure属性を付与
  if (typeof location !== 'undefined' && location.protocol === 'https:') {
    parts.push('Secure');
  }
  return parts.join('; ');
}

/**
 * 同意状態（id/categories/timestamp）のみ削除し、geoフィールドは保持する
 * カテゴリ変更等で陳腐化した同意を破棄する際に、リージョン判定結果まで消さないために使う
 * @param {string} cookieName
 * @param {Object} cookieOptions - { maxAge, path, sameSite }
 */
export function clearConsentKeepGeo(cookieName, cookieOptions) {
  memoryFallback = null;

  const raw = readRaw(cookieName);
  if (raw && raw.geo) {
    try {
      document.cookie = buildCookieString(cookieName, { geo: raw.geo }, cookieOptions);
    } catch {
      // 書き込み失敗時は何もしない（古い同意はreadConsent側で読めても呼び出し側が無視する）
    }
  } else {
    deleteConsent(cookieName, cookieOptions.path);
  }
}

/**
 * Cookieを削除する
 * @param {string} cookieName
 * @param {string} path
 */
export function deleteConsent(cookieName, path) {
  document.cookie = `${cookieName}=; max-age=0; path=${path}`;
  memoryFallback = null;
}

/**
 * メモリフォールバックを明示的にリセットする（テスト・再初期化用）
 */
export function resetMemoryFallback() {
  memoryFallback = null;
}
